import { useEffect, useRef, useState } from 'react';
import { useGameSocket } from './hooks/useGameSocket';
import { useGameStore, useMe, useMyPiece, usePhase, usePrivateState, useRoomCode, useSnapshot } from './store/gameStore';
import Lobby from './components/Lobby';
import Board from './components/Board';
import Chat from './components/Chat';
import GameOverModal from './components/GameOverModal';
import type { PlayerPublic } from '../../shared/types';

function PieceBadge({ piece, size = 'text-lg' }: { piece: string | null; size?: string }) {
  if (!piece) return null;
  return (
    <span className={`${size} font-display font-bold ${piece === 'X' ? 'text-xpiece' : 'text-opiece'}`}>
      {piece}
    </span>
  );
}

function PlayerRow({ player, isCurrent, isMe }: { player: PlayerPublic; isCurrent: boolean; isMe: boolean }) {
  const isBot = !!player.isBot;
  return (
    <div
      className={[
        'flex items-center gap-3 rounded-xl px-3.5 py-2.5 border transition-all',
        isCurrent
          ? 'border-ttt-accent/60 bg-ttt-accent/10 ttt-glow-pulse'
          : 'border-white/10 bg-white/5',
      ].join(' ')}
    >
      <PieceBadge piece={player.piece} />
      <span className="flex-1 truncate text-ttt-ink font-medium">
        {isBot && <span className="mr-1">🤖</span>}
        {player.name}
        {isMe && <span className="text-ttt-muted text-sm"> (you)</span>}
        {isBot && (
          <span className="ml-1.5 rounded-full bg-white/10 px-1.5 py-px text-[10px] font-bold uppercase text-ttt-muted">
            {player.botDifficulty ?? 'bot'}
          </span>
        )}
        {!isBot && !player.connected && <span className="text-ttt-muted text-sm"> · reconnecting…</span>}
      </span>
      <span className="text-ttt-muted text-sm tabular-nums" title="Wins this session">
        ★ {player.wins}
      </span>
    </div>
  );
}

export default function App() {
  const api = useGameSocket();
  const connected = useGameStore((s) => s.connected);
  const error = useGameStore((s) => s.error);
  const roomCode = useRoomCode();
  const phase = usePhase();
  const snapshot = useSnapshot();
  const me = useMe();
  const myPiece = useMyPiece();
  const privateState = usePrivateState();
  const myPlayerId = useGameStore((s) => s.myPlayerId);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [botDifficulty, setBotDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

  // Pop the result modal on every game end; hide it again on rematch.
  useEffect(() => {
    setResultOpen(snapshot?.phase === 'gameover');
  }, [snapshot?.phase, snapshot?.sequence]);

  const copyTimer = useRef<number | null>(null);
  const copyText = async (text: string, what: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for non-secure contexts where the async clipboard API is unavailable.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(what);
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(null), 1600);
  };

  // Keep the room code in the URL so a refresh / share restores the seat.
  useEffect(() => {
    if (!roomCode) return;
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    window.history.replaceState(null, '', `${base}/room/${roomCode}`);
  }, [roomCode]);

  // Auto-join when landing on /room/CODE (shared link or refresh).
  useEffect(() => {
    const match = window.location.pathname.match(/\/room\/([A-Z0-9]{6})$/i);
    if (!match) return;
    const code = match[1].toUpperCase();
    const name = localStorage.getItem('ttt-name');
    if (code && name && !useGameStore.getState().roomCode) {
      api.joinRoom(code, name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No snapshot yet → lobby. Keep the lobby mounted even while the socket is
  // down (only show a hint), otherwise it would be replaced by a permanent
  // "Connecting…" screen the moment the WS connects but no room is joined.
  if (!snapshot) {
    return (
      <main className="min-h-full flex flex-col items-center justify-center py-10 bg-gradient-to-b from-ttt-bg via-ttt-bg-2 to-ttt-bg">
        <Lobby onCreateRoom={api.createRoom} onJoinRoom={api.joinRoom} />
        {!connected && (
          <p className="text-ttt-muted/60 text-xs mt-6">Connecting to server…</p>
        )}
        {error && (
          <p className="mt-3 rounded-lg bg-xpiece/15 border border-xpiece/40 text-xpiece-light px-4 py-2 text-sm max-w-md mx-4 text-center">
            {error}
          </p>
        )}
      </main>
    );
  }

  const players = snapshot.players;
  const isHost = !!me && snapshot.hostId === me.id;
  const opponent = players.find((p) => p.id !== me?.id) ?? null;
  const bot = players.find((p) => p.isBot) ?? null;
  const iWon = !!me && snapshot.winnerId === me.id;
  const currentPlayer = players.find((p) => p.id === snapshot.currentPlayerId) ?? null;

  return (
    <main className="min-h-full bg-gradient-to-b from-ttt-bg via-ttt-bg-2 to-ttt-bg px-4 py-6 lg:py-10">
      <div className="max-w-4xl mx-auto flex flex-col gap-5">
        {/* Header */}
        <header className="flex items-center justify-between gap-3">
          <h1 className="font-display font-bold text-xl text-ttt-ink flex items-center gap-2 flex-wrap">
            Tic Tac Toe
            <button
              onClick={() => copyText(snapshot.roomCode, 'code')}
              title="Copy room code"
              className="font-mono text-sm tracking-[0.25em] text-ttt-accent bg-ttt-accent/10 border border-ttt-accent/30 rounded-lg px-2.5 py-0.5 hover:bg-ttt-accent/20 active:scale-95 transition"
            >
              {snapshot.roomCode}
              <span className="ml-1.5 tracking-normal">{copied === 'code' ? '✓' : '⧉'}</span>
            </button>
            {copied === 'code' && (
              <span className="text-xs font-body font-medium text-opiece">Code copied!</span>
            )}
          </h1>
          <button
            onClick={api.leave}
            className="text-ttt-muted text-sm hover:text-ttt-ink transition-colors"
          >
            Leave room
          </button>
        </header>

        {/* Room code — prominent card with an explicit copy button */}
        <div className="ttt-glass rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-ttt-muted text-[10px] uppercase tracking-wider font-semibold">
              Room code
            </p>
            <p className="font-mono text-xl sm:text-2xl tracking-[0.3em] text-ttt-accent font-semibold leading-tight">
              {snapshot.roomCode}
            </p>
          </div>
          <button
            onClick={() => copyText(snapshot.roomCode, 'code')}
            className={[
              'shrink-0 rounded-xl px-4 py-2.5 font-display font-semibold text-sm transition active:scale-95',
              copied === 'code'
                ? 'bg-opiece/20 border border-opiece/50 text-opiece-light'
                : 'bg-gradient-to-r from-ttt-accent to-ttt-accent-dark text-ttt-bg hover:brightness-110 shadow-glow',
            ].join(' ')}
          >
            {copied === 'code' ? '✓ Copied!' : '⧉ Copy Code'}
          </button>
          <button
            onClick={() => copyText(window.location.href, 'link')}
            title="Copy invite link"
            className="shrink-0 rounded-xl px-3 py-2.5 border border-ttt-border bg-white/5 text-ttt-muted hover:text-ttt-ink hover:bg-white/10 transition active:scale-95"
          >
            {copied === 'link' ? '✓' : '🔗'}
          </button>
        </div>

        {/* Status banner */}
        <div
          className={[
            'rounded-xl px-4 py-3 text-center font-display font-semibold',
            phase === 'gameover'
              ? iWon
                ? 'bg-gradient-to-r from-opiece/30 to-ttt-accent/30 text-ttt-ink border border-ttt-accent/40'
                : 'bg-white/5 text-ttt-muted border border-white/10'
              : privateState?.youCanMove
                ? 'ttt-gradient bg-gradient-to-r from-ttt-accent to-xpiece text-ttt-bg'
                : 'bg-white/5 text-ttt-muted border border-white/10',
          ].join(' ')}
        >
          {phase === 'lobby' && 'Waiting in lobby — mark yourself ready!'}
          {phase === 'playing' &&
            (privateState?.youCanMove
              ? `Your turn — you are ${myPiece ?? '?'}`
              : currentPlayer
                ? `${currentPlayer.isBot ? '🤖 ' : ''}${currentPlayer.name}'s turn`
                : "Opponent's turn")}
          {phase === 'gameover' &&
            (snapshot.draw
              ? "It's a draw!"
              : iWon
                ? 'You win! 🎉'
                : `${players.find((p) => p.id === snapshot.winnerId)?.name ?? 'Opponent'} wins`)}
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5 items-start">
          <div className="flex flex-col items-center gap-5 min-w-0">
            {/* Players */}
            <div className="w-full max-w-[420px] flex flex-col gap-2">
              {players.map((p) => (
                <PlayerRow
                  key={p.id}
                  player={p}
                  isCurrent={phase === 'playing' && snapshot.currentPlayerId === p.id}
                  isMe={p.id === myPlayerId}
                />
              ))}
            </div>

            {/* Board */}
            {(phase === 'playing' || phase === 'gameover') && snapshot && (
              <Board
                snapshot={snapshot}
                myPiece={myPiece}
                youCanMove={!!privateState?.youCanMove}
                onMove={api.makeMove}
              />
            )}

            {/* Lobby actions */}
            {phase === 'lobby' && (
              <div className="w-full max-w-[420px] flex flex-col gap-3">
                <button
                  onClick={() => api.setReady(!(me?.ready ?? false))}
                  className={[
                    'rounded-xl px-4 py-3 font-display font-semibold transition active:scale-[0.98]',
                    me?.ready
                      ? 'bg-opiece/20 border border-opiece/50 text-opiece-light'
                      : 'bg-white/5 border border-ttt-border text-ttt-ink hover:bg-white/10',
                  ].join(' ')}
                >
                  {me?.ready ? '✓ Ready' : 'Mark Ready'}
                </button>
                {/* Bot controls (host only, lobby only) */}
                {isHost && !bot && players.length < 2 && (
                  <>
                    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
                      <span className="shrink-0 text-[11px] font-bold uppercase tracking-widest text-ttt-muted">
                        🤖 Add bot
                      </span>
                      <select
                        value={botDifficulty}
                        onChange={(e) => setBotDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                        aria-label="Bot difficulty"
                        className="h-9 min-w-0 flex-1 cursor-pointer rounded-lg border border-ttt-border bg-ttt-bg/70 px-2 text-sm font-semibold text-ttt-ink outline-none transition hover:bg-white/10 focus:border-ttt-accent"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                      <button
                        onClick={() => api.addBot(botDifficulty)}
                        className="h-9 shrink-0 rounded-lg bg-white/10 px-4 text-sm font-bold text-ttt-ink ring-1 ring-white/15 transition hover:bg-white/20 active:scale-[0.98]"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-center text-ttt-muted text-xs">
                      Playing solo? Add a bot to start instantly — no friend needed.
                    </p>
                  </>
                )}
                {isHost && bot && (
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-ttt-ink">
                      <span className="mr-1">🤖</span>{bot.name}
                    </span>
                    <select
                      value={bot.botDifficulty ?? 'medium'}
                      onChange={(e) => api.setBotDifficulty(bot.id, e.target.value as 'easy' | 'medium' | 'hard')}
                      aria-label={`Bot difficulty for ${bot.name}`}
                      className="h-9 cursor-pointer rounded-lg border border-ttt-border bg-ttt-bg/70 px-2 text-sm font-semibold text-ttt-ink outline-none transition hover:bg-white/10 focus:border-ttt-accent"
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                    <button
                      onClick={() => api.removeBot(bot.id)}
                      title={`Remove ${bot.name}`}
                      aria-label={`Remove ${bot.name}`}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-ttt-muted transition hover:border-xpiece/50 hover:text-xpiece-light"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {isHost ? (
                  <button
                    disabled={!players.every((p) => p.ready) || players.length < 2}
                    onClick={api.startGame}
                    className="ttt-gradient rounded-xl px-4 py-3.5 font-display font-semibold text-lg text-ttt-bg bg-gradient-to-r from-ttt-accent to-opiece disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98] transition shadow-glow"
                  >
                    Start Game
                  </button>
                ) : (
                  <p className="text-center text-ttt-muted text-sm">
                    Waiting for host to start…
                    {!opponent && ' Share the room code with a friend!'}
                  </p>
                )}
              </div>
            )}

            {/* Result — full modal on game end; compact reopen button once dismissed */}
            {phase === 'gameover' && !resultOpen && (
              <button
                onClick={() => setResultOpen(true)}
                className="ttt-gradient rounded-xl px-5 py-2.5 font-display font-semibold text-ttt-bg bg-gradient-to-r from-ttt-accent to-opiece hover:brightness-110 active:scale-[0.98] transition shadow-glow"
              >
                🏆 Show result
              </button>
            )}
          </div>

          {/* Chat */}
          <Chat chat={snapshot.chat} onSend={api.sendChat} />
        </div>

        {error && (
          <p className="mx-auto rounded-lg bg-xpiece/15 border border-xpiece/40 text-xpiece-light px-4 py-2 text-sm text-center max-w-md">
            {error}
          </p>
        )}
      </div>

      {/* Game-over modal */}
      {phase === 'gameover' && resultOpen && (
        <GameOverModal
          snapshot={snapshot}
          myPlayerId={myPlayerId}
          isHost={isHost}
          onPlayAgain={api.playAgain}
          onDismiss={() => setResultOpen(false)}
        />
      )}
    </main>
  );
}
